import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { CandidatesConst } from '@axiom/const';
import { MlRecommendedUtil } from '@axiom/utils';
import type {
  Candidate,
  MLRecommendation,
  Opportunity,
  OpportunityCandidate,
  Position,
} from '@axiom/validation';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardSection,
  CondensedMedium,
  Flashy,
  Grid,
  GridColumn,
  GridRow,
  Layout,
  SmallHeader,
  ToastUtil,
  CondensedHeader,
  CondensedLarge,
  Gutter,
  LayoutItem,
} from '@axiom/ui';

import type { FormSchema } from '../GetMoreMatchesModal/GetMoreMatchesModal';
import { GetMoreMatchesModal } from '../GetMoreMatchesModal/GetMoreMatchesModal';
import { MlRecommendationsApi } from '../../api/ml-recommendations-api';
import { OpportunityStore } from '../../stores/opportunity-store';
import { AddTalentUtil } from '../../utils/AddTalentUtil';
import { AccountBenchApi } from '../../api/account-bench-api';

import { OpportunityTalentParentPositionEmpty } from './OpportunityTalentParentPositionEmpty';
import { OpportunityTalentParentPositionList } from './OpportunityTalentParentPositionList';
import { OTScrollable } from './OpportunityTalentParentPositionStyles';
import { OpportunityTalentFindCandidateModal } from './OpportunityTalentFindCandidateModal';

const {
  ProfileStatuses: { Active, Beach, PendingActive, PendingBeach, Waitlist },
} = CandidatesConst;

interface OpportunityTalentParentPositionProps {
  name: string;
  title?: string;
  opportunity: Opportunity;
  candidates: OpportunityCandidate[];
  hasBeenAiMatched?: boolean;
  isFilled?: boolean;
  isTalentPool?: boolean;
  mlRecommendations?: MLRecommendation[];
  positionId?: Position['id'];
  positions?: Position[];
}

export const OpportunityTalentParentPosition: FC<
  OpportunityTalentParentPositionProps
> = ({
  name,
  title,
  opportunity,
  candidates,
  hasBeenAiMatched,
  isFilled,
  isTalentPool,
  mlRecommendations,
  positionId,
  positions,
}) => {
  const aiUnmatchableMessage =
    MlRecommendedUtil.getAiUnmatchableMessage(opportunity);

  const isAiMatchable = !aiUnmatchableMessage;

  const [showFindCandidateModal, setShowFindCandidateModal] = useState(false);
  const [showGetMoreMatchesModal, setShowGetMoreMatchesModal] = useState(false);
  const [shouldTriggerModal, setShouldTriggerModal] =
    useState(hasBeenAiMatched);
  const [savedFilters, setSavedFilters] = useState<FormSchema>({});
  const [newlyAddedCandidateId, setNewlyAddedCandidateId] = useState<
    Candidate['id'] | null
  >(null);

  useEffect(() => {
    if (newlyAddedCandidateId) {
      AddTalentUtil.handleWarnings(candidates, newlyAddedCandidateId);
    }
  }, [newlyAddedCandidateId]);

  const onGetAiMatchesClick = async () => {
    if (shouldTriggerModal) {
      setShowGetMoreMatchesModal(true);
    } else {
      const { data: recommendationCount } =
        await MlRecommendationsApi.createRecommendations({
          opportunityId: opportunity.id,
          filters: {
            addressCountryCode: opportunity.requiredTalentCountryCode,
            businessTitles: MlRecommendedUtil.getDesiredBusinessTitles(
              opportunity.positions
            ),
            profileStatus: [
              Active,
              Beach,
              PendingActive,
              PendingBeach,
              Waitlist,
            ],
            weeklyAvailability:
              MlRecommendedUtil.getRequiredBillingHoursPerWeek(
                opportunity.positions
              ),
            size: 3,
          },
        });

      await MlRecommendationsApi.refreshRecommendations(opportunity.id);

      await OpportunityStore.load(opportunity.id);
      if (recommendationCount === 0) {
        ToastUtil.add({
          dismissible: true,
          name: `NOMATCHES`,
          type: 'error',
          children: (
            <>
              <CondensedHeader>Error</CondensedHeader>
              <CondensedLarge>
                We didn't find available talent who match up with all of this
                opportunity’s requirements.
              </CondensedLarge>
            </>
          ),
        });
      } else {
        setShouldTriggerModal(true);
      }
    }
  };

  const displayAiMatchesSection = () => {
    if (opportunity.isFulfillmentActive && !!isTalentPool && isAiMatchable) {
      return (
        <LayoutItem>
          <Button
            name="GET_AI_MATCHES_BUTTON"
            icon="intelligence-filled"
            iconPosition="right"
            onClick={onGetAiMatchesClick}
            pattern="brand"
          >
            {hasBeenAiMatched ? 'Refine Matches' : 'AI Matches'}
          </Button>
        </LayoutItem>
      );
    } else if (isFilled) {
      return (
        <LayoutItem>
          <Badge background="green" name="filled-badge">
            Filled
          </Badge>
        </LayoutItem>
      );
    }

    return null;
  };

  return (
    <Card name={name}>
      <CardHeader>
        <Grid>
          <GridRow>
            <GridColumn>
              <Layout
                direction="horizontal"
                horizontalGutter="8px"
                position="middle"
              >
                <LayoutItem>
                  <Badge background="gray" name="candidate-count-badge">
                    {candidates.length + (mlRecommendations?.length ?? 0)}
                  </Badge>
                </LayoutItem>
                <LayoutItem fluid>
                  <SmallHeader maxLines={1} name={`${name}-card-title`}>
                    {title}
                  </SmallHeader>
                </LayoutItem>
                {displayAiMatchesSection()}
              </Layout>
            </GridColumn>
          </GridRow>
        </Grid>
      </CardHeader>
      <OTScrollable tall={isTalentPool}>
        <CardSection>
          {!!isTalentPool && !isAiMatchable && (
            <Gutter bottom="16px">
              <CondensedMedium name="AI_UNMATCHABLE_MESSAGE">
                <Flashy bold>Note:</Flashy> {aiUnmatchableMessage}
              </CondensedMedium>
            </Gutter>
          )}
          {opportunity.isFulfillmentActive &&
          !(
            opportunity.selfService &&
            !opportunity.isSynced &&
            !isTalentPool
          ) ? (
            <Gutter bottom="16px">
              <Layout direction="vertical">
                <Button
                  name="ADD_TALENT_BUTTON"
                  variation="outline"
                  pattern="secondary"
                  onClick={() => {
                    setShowFindCandidateModal(true);
                  }}
                >
                  Add Talent
                </Button>
              </Layout>
            </Gutter>
          ) : null}
          {candidates.length === 0 && (mlRecommendations?.length ?? 0) === 0 ? (
            <OpportunityTalentParentPositionEmpty isTalentPool={isTalentPool} />
          ) : (
            <OpportunityTalentParentPositionList
              opportunity={opportunity}
              opportunityCandidates={candidates}
              isTalentPool={isTalentPool}
              mlRecommendations={mlRecommendations}
            />
          )}
        </CardSection>
      </OTScrollable>
      {showFindCandidateModal ? (
        <OpportunityTalentFindCandidateModal
          onClose={(candidateId: Candidate['id'] | null) => {
            setShowFindCandidateModal(false);
            setNewlyAddedCandidateId(candidateId);
            void AccountBenchApi.refreshOpportunityBench({
              opportunityId: opportunity.id,
            });
          }}
          opportunityId={opportunity.id}
          positionId={positionId}
          positions={positions || []}
        />
      ) : null}
      {showGetMoreMatchesModal ? (
        <GetMoreMatchesModal
          opportunity={opportunity}
          onRequestSuccess={({ filters }) => {
            setSavedFilters(filters);
          }}
          onClose={() => {
            setShowGetMoreMatchesModal(false);
          }}
          savedFilters={savedFilters}
        />
      ) : null}
    </Card>
  );
};
